/**
 * 業務の受け入れ条件（`acceptance`）の入力スキーマ。
 *
 * 受け入れ条件は、業務が何を大事にするか（思想）を**ルールの並び**で持ち、ルールごとに why を
 * 添える。書き方は Gherkin の「機能 ＝ 業務、ルール ＝ 思想の 1 行」に倣い、シナリオ（例）は
 * 持たない。業務ルール（判定表・並べ替え・式）が「どう決めるか」を持つのに対し、受け入れ条件は
 * 「何を大事にし、なぜそうするか」を持つ。
 *
 * **優先の順位は番号ではなく並び順で持つ。** 種類が `priority` のルールの並び順が、そのまま優先の
 * 順位である。番号で持つと、1 行足すたびに下の行の番号が全部ずれて、案件の差分に中身の変わって
 * いない行が混ざる。
 *
 * **ルールの文と why の本文は自由文である。** 用語を「」で囲む書き方をするが、囲んだ語が用語集に
 * 在るかはここでは見ない。
 *
 * 業務の中で決まる検査（目標の参照が在るか・実現している業務ルールがその業務の業務ルールか）は
 * 業務の欄どうしを突き合わせるので `checkBusinessAcceptance` に置き、書込と監査の形の検査
 * （`checkBusinessShape`）から呼ぶ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務の受け入れ条件（目標・ルールの種類・ルールの文・why・実現している業務ルール）の zod 入力スキーマ
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 受け入れ条件のルールの種類 4 つ。
 *
 * - `guard` 守る線（対象にする ・ しない、同時にいくつまで）
 * - `exception` 例外（守る線にかかわらず、〜なら〜する）
 * - `resume` やりかけ（途中のものがあれば最初にする）
 * - `priority` 優先（並び順がそのまま優先の順位）
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件のルールの種類（守る線・例外・やりかけ・優先）の allowlist
 * @graph-connects none
 */
export const ACCEPTANCE_RULE_KINDS = ['guard', 'exception', 'resume', 'priority'];
/**
 * 仮説の検証の状態 3 つ。`unverified` 未検証 / `verifying` 検証中 / `confirmed` 確認済み。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件の仮説の検証の状態（未検証・検証中・確認済み）の allowlist
 * @graph-connects none
 */
export const ACCEPTANCE_HYPOTHESIS_STATUSES = ['unverified', 'verifying', 'confirmed'];
/**
 * 補足 1 件の字数の上限。長い説明が要るなら、補足ではなく困りごと ・ 仮説 ・ 根拠の欄が
 * 足りていないしるしなので、補足を長くしない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件の補足 1 件の字数の上限
 * @graph-connects none
 */
export const ACCEPTANCE_NOTE_MAX_LENGTH = 40;
/**
 * 目標の鍵の書式。`G` のあとに 1 から始まる番号（`G1` / `G12`）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件の目標の鍵（G1 / G2 …）の書式
 * @graph-connects none
 */
export const ACCEPTANCE_GOAL_KEY_PATTERN = /^G[1-9]\d*$/;
/** 空白だけでない本文。 @graph-connects none */
const acceptanceText = z.string().trim().min(1, '空の本文は書けない');
/** 目標の鍵。 @graph-connects none */
const acceptanceGoalKey = z
    .string()
    .regex(ACCEPTANCE_GOAL_KEY_PATTERN, '目標の鍵は G のあとに 1 から始まる番号（G1 / G2 …）');
/**
 * 目標 1 件（鍵 ＋ 本文）。ルールからは鍵で指す。同じ目標を複数のルールが指してよい。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件の目標 1 件（鍵と本文）の入力スキーマ
 * @graph-connects none
 */
export const acceptanceGoalSchema = z.strictObject({
    key: acceptanceGoalKey,
    text: acceptanceText,
});
/**
 * 仮説 1 件（検証の状態 ＋ 本文）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件の仮説 1 件（検証の状態と本文）の入力スキーマ
 * @graph-connects none
 */
export const acceptanceHypothesisSchema = z.strictObject({
    status: z.enum(ACCEPTANCE_HYPOTHESIS_STATUSES),
    text: acceptanceText,
});
/**
 * 根拠 1 件（出典 ＋ 原文の引用）。引用は書き手の文ではなく出典の写しである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件の根拠 1 件（出典と原文の引用）の入力スキーマ
 * @graph-connects none
 */
export const acceptanceEvidenceSchema = z.strictObject({
    source: acceptanceText,
    quote: acceptanceText,
});
/**
 * 受け入れ条件のルール 1 本。種類 ・ 文 ・ why ・ 実現している業務ルール ・ 要確認。
 *
 * why の欄（`goals` / `troubles` / `measures` / `hypotheses` / `evidences` / `notes`）はどれも
 * 省いてよい。why が分からないルールは空のまま置き、文で埋めない。
 *
 * `realizes` は、このルールを実現している業務ルールの id である。同じ呼び出しで作る業務ルールの
 * 仮の番号は指せない（業務ルールを先に書く）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 受け入れ条件のルール 1 本（種類・文・why・実現している業務ルール・要確認）の入力スキーマ
 * @graph-connects none
 */
export const acceptanceRuleSchema = z.strictObject({
    kind: z.enum(ACCEPTANCE_RULE_KINDS),
    text: acceptanceText,
    goals: z.array(acceptanceGoalKey).optional(),
    troubles: z.array(acceptanceText).optional(),
    measures: z.array(acceptanceText).optional(),
    hypotheses: z.array(acceptanceHypothesisSchema).optional(),
    evidences: z.array(acceptanceEvidenceSchema).optional(),
    notes: z
        .array(acceptanceText.max(ACCEPTANCE_NOTE_MAX_LENGTH, `補足は ${String(ACCEPTANCE_NOTE_MAX_LENGTH)} 字以内。長い説明は困りごと ・ 仮説 ・ 根拠に書く`))
        .optional(),
    realizes: z
        .array(z.number().int().positive('実現している業務ルールは既に在る業務ルールの id'))
        .optional(),
    needsReview: z.boolean().optional(),
});
/**
 * 業務の受け入れ条件。目標の並びとルールの並び。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務の受け入れ条件（目標の並びとルールの並び）の入力スキーマ
 * @graph-connects none
 */
export const businessAcceptanceSchema = z.strictObject({
    goals: z.array(acceptanceGoalSchema),
    rules: z.array(acceptanceRuleSchema),
});
/**
 * 業務の欄どうしを突き合わせて、受け入れ条件の不備を文言で返す。無ければ空。
 *
 * 見るのは 3 つ。①目標の鍵が重ならない ②ルールが指す目標が在る ③実現している業務ルールが、
 * その業務の業務ルールの集合に在り、1 本のルールの中で重ならない。
 *
 * @param acceptance 業務の受け入れ条件
 * @param businessRules その業務の業務ルールの id の集合
 * @returns 不備の文言。無ければ空
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務の受け入れ条件を業務の欄と突き合わせ、目標の重なり・指す目標の不在・業務の外の業務ルールを文言で返す
 * @graph-connects none
 */
export function checkBusinessAcceptance(acceptance, businessRules) {
    const problems = [];
    const goalKeys = new Set();
    for (const goal of acceptance.goals) {
        if (goalKeys.has(goal.key))
            problems.push(`受け入れ条件の目標 ${goal.key} が 2 つある`);
        goalKeys.add(goal.key);
    }
    const ruleIds = new Set(businessRules);
    acceptance.rules.forEach((rule, index) => {
        const at = `受け入れ条件のルール ${String(index + 1)} 本目`;
        for (const key of rule.goals ?? []) {
            if (!goalKeys.has(key))
                problems.push(`${at}が指す目標 ${key} が目標の並びに無い`);
        }
        const realizes = rule.realizes ?? [];
        if (new Set(realizes).size !== realizes.length) {
            problems.push(`${at}の実現している業務ルールに同じ id が 2 つある`);
        }
        for (const id of realizes) {
            if (!ruleIds.has(id)) {
                problems.push(`${at}の実現している業務ルール ${String(id)} は、この業務の業務ルールに無い`);
            }
        }
    });
    return problems;
}
//# sourceMappingURL=business-acceptance.js.map